import * as THREE from 'three';
import {
  INTERPOLATION_DELAY_MS,
  PLAYER_CROUCH_HEIGHT,
  PLAYER_HEIGHT,
  PLAYER_RADIUS,
  TICK_DT,
  WEAPON_ORDER,
  characterFromIndex,
  clamp,
  lerp,
  lerpAngle,
  weaponIndex,
  type PlayerId,
  type TeamId,
} from '@game/shared';
import { SnapshotFlag, type SnapshotPlayer } from '@game/protocol';

interface Sample {
  timeMs: number;
  x: number;
  y: number;
  z: number;
  yaw: number;
  pitch: number;
  crouched: boolean;
  grounded: boolean;
  sliding: boolean;
}

interface RemotePlayer {
  id: PlayerId;
  team: TeamId;
  name: string;
  character: number;
  rig: CharacterRig;
  label: THREE.Sprite;
  samples: Sample[];
  health: number;
  alive: boolean;
  /** Index into `rig.guns` of the weapon currently held. */
  weapon: number;
  /** Limb-swing phase, advanced by distance travelled so the gait matches speed. */
  walkPhase: number;
  /** 0 standing → 1 crouched, eased so the kneel is a motion, not a snap. */
  crouchAmount: number;
  /** 0 upright → 1 sliding, eased fast — drives the baseball-slide pose. */
  slideAmount: number;
  /** 0 grounded → 1 airborne, eased — drives the jump tuck. */
  airAmount: number;
  /** Landing squash impulse, decayed exponentially after touchdown. */
  landDip: number;
  prevGrounded: boolean;
  lastX: number;
  lastZ: number;
  /** Interpolated eye pose from the last update, for the spectator camera. */
  eyeX: number;
  eyeY: number;
  eyeZ: number;
  viewYaw: number;
  viewPitch: number;
}

/** Filled by `spectatePose` — preallocate one and reuse it per frame. */
export interface SpectatePose {
  x: number;
  y: number;
  z: number;
  yaw: number;
  pitch: number;
}

export const TEAM_COLORS: Record<TeamId, number> = { 0: 0x4cc2ff, 1: 0xff6b5e };

// Body proportions, sized to fill the authoritative capsule (1.8 m tall). The
// hitboxes live in @game/sim; these are visuals matching them closely enough
// that aiming at what you see is aiming at the hitbox.
const LEG_HEIGHT = 0.72;
const THIGH_LENGTH = 0.38;
const SHIN_LENGTH = LEG_HEIGHT - THIGH_LENGTH;
const TORSO_HEIGHT = 0.6;
const TORSO_TOP = LEG_HEIGHT + TORSO_HEIGHT; // 1.32
const ARM_HEIGHT = 0.55;
const HEAD_SIZE = 0.36;
const HEAD_CENTER_Y = 1.56;

// Crouch is a squat, articulated like a real one: thighs rotate forward at the
// hips, shins bend back at the knees so the feet stay planted under the body,
// and the hip height falls out of that leg geometry rather than being a
// separate number. Angles are at full crouch, blended linearly by the amount.
const CROUCH_THIGH = 1.1; // thigh forward of vertical — knees out in front
const CROUCH_SHIN = 0.55; // shin tucked back of vertical — heels under the hips
const CROUCH_LEAN = 0.26;
const CROUCH_HIP_BACK = 0.12; // hips shift behind the feet, like a real squat
const CROUCH_KNEE_OUT = 0.14; // thighs splay so the knees don't clip together
const CROUCH_ARM = 0.5; // arms reach forward over the knees for balance

// The slide pose: hips skimming the floor, lead leg kicked out straight in
// front, trailing leg folded under, torso tipped back with the free arm
// planted behind — the baseball-slide silhouette. Values are world targets the
// pose eases toward, layered over whatever the crouch pose set.
const SLIDE_HIP_Y = 0.3;
const SLIDE_LEAN_BACK = 0.5;

/** (width, height, length) per weapon, indexed like `WEAPON_ORDER`. */
const GUN_SIZES: Record<(typeof WEAPON_ORDER)[number], [number, number, number]> = {
  smg: [0.09, 0.11, 0.42],
  rifle: [0.09, 0.12, 0.62],
  shotgun: [0.11, 0.14, 0.66],
  sniper: [0.08, 0.11, 0.8],
};

/**
 * A third-person character: the world-space `group`, the crouch-squashed `rig`
 * inside it, swingable limbs, and one gun model per weapon slot parented to the
 * right arm — raising that arm (rotation.x = π/2 + pitch) turns the character
 * into an aiming shooter, with `muzzles` marking where each barrel ends.
 *
 * Shared by the remote-player renderer and the lobby's local avatar, so the
 * model you inspect between matches is exactly the one everyone else sees.
 */
export interface CharacterRig {
  group: THREE.Group;
  /** Everything except the nameplate. */
  rig: THREE.Group;
  /**
   * Torso, head and arms, pivoted at the hips — `applyCrouchPose` drops and
   * leans this as one piece while the legs fold underneath.
   */
  upperBody: THREE.Group;
  leftArm: THREE.Object3D;
  rightArm: THREE.Object3D;
  /** Thigh pivots at the hips; walk swing and the squat rotate these. */
  leftLeg: THREE.Object3D;
  rightLeg: THREE.Object3D;
  /** Knee pivots inside the legs; only the crouch pose bends them. */
  leftKnee: THREE.Object3D;
  rightKnee: THREE.Object3D;
  /** Hidden by default; show the held one. Indexed like `WEAPON_ORDER`. */
  guns: THREE.Group[];
  /** Barrel tips of `guns`, for spawning tracers at the visible muzzle. */
  muzzles: THREE.Object3D[];
  /** Owned by this rig; disposed with it. */
  resources: (THREE.BufferGeometry | THREE.Material)[];
}

export const buildCharacterRig = (
  characterIndex: number,
  teamColor: number,
): CharacterRig => {
  const def = characterFromIndex(characterIndex);

  const group = new THREE.Group();
  const rig = new THREE.Group();
  group.add(rig);
  // The hip pivot: everything above the waist hangs off this, so crouching can
  // drop and lean the upper body as one piece.
  const upperBody = new THREE.Group();
  upperBody.position.y = LEG_HEIGHT;
  rig.add(upperBody);
  const resources: (THREE.BufferGeometry | THREE.Material)[] = [];

  const material = (color: number, options: Partial<THREE.MeshLambertMaterialParameters> = {}) => {
    const m = new THREE.MeshLambertMaterial({ color, ...options });
    resources.push(m);
    return m;
  };
  const geometry = <G extends THREE.BufferGeometry>(g: G): G => {
    resources.push(g);
    return g;
  };
  const block = (
    w: number,
    h: number,
    d: number,
    color: number,
    x: number,
    y: number,
    z: number,
    parent: THREE.Object3D = rig,
  ): THREE.Mesh => {
    const mesh = new THREE.Mesh(geometry(new THREE.BoxGeometry(w, h, d)), material(color));
    mesh.position.set(x, y, z);
    parent.add(mesh);
    return mesh;
  };
  /** Same character-space coordinates as `block`, parented to the hip pivot. */
  const upperBlock = (
    w: number,
    h: number,
    d: number,
    color: number,
    x: number,
    y: number,
    z: number,
  ): THREE.Mesh => block(w, h, d, color, x, y - LEG_HEIGHT, z, upperBody);

  /**
   * A limb that swings from its top: the geometry hangs below the pivot, so
   * rotating the pivot around X is a natural walk swing.
   */
  const limb = (
    w: number,
    h: number,
    d: number,
    color: number,
    x: number,
    pivotY: number,
    parent: THREE.Object3D = rig,
  ): THREE.Object3D => {
    const pivot = new THREE.Group();
    pivot.position.set(x, pivotY, 0);
    const mesh = new THREE.Mesh(geometry(new THREE.BoxGeometry(w, h, d)), material(color));
    mesh.position.y = -h / 2;
    pivot.add(mesh);
    parent.add(pivot);
    return pivot;
  };

  /**
   * A two-segment leg: thigh pivoting at the hip, shin pivoting at a knee
   * inside it. The extra 0.04 on each box overlaps the joints so bending never
   * opens a visible gap between the segments.
   */
  const leg = (x: number): { hip: THREE.Object3D; knee: THREE.Object3D } => {
    const hip = new THREE.Group();
    hip.position.set(x, LEG_HEIGHT, 0);
    const thigh = new THREE.Mesh(
      geometry(new THREE.BoxGeometry(0.17, THIGH_LENGTH + 0.04, 0.22)),
      material(def.legs),
    );
    thigh.position.y = -THIGH_LENGTH / 2;
    hip.add(thigh);
    const knee = new THREE.Group();
    knee.position.y = -THIGH_LENGTH;
    const shin = new THREE.Mesh(
      geometry(new THREE.BoxGeometry(0.16, SHIN_LENGTH + 0.04, 0.2)),
      material(def.legs),
    );
    shin.position.y = -SHIN_LENGTH / 2;
    knee.add(shin);
    hip.add(knee);
    rig.add(hip);
    return { hip, knee };
  };

  const left = leg(-0.11);
  const right = leg(0.11);
  const leftLeg = left.hip;
  const rightLeg = right.hip;
  upperBlock(0.5, TORSO_HEIGHT, 0.28, def.torso, 0, LEG_HEIGHT + TORSO_HEIGHT / 2, 0);
  const armPivotY = TORSO_TOP - 0.05 - LEG_HEIGHT;
  const leftArm = limb(0.14, ARM_HEIGHT, 0.2, def.sleeves, -0.33, armPivotY, upperBody);
  const rightArm = limb(0.14, ARM_HEIGHT, 0.2, def.sleeves, 0.33, armPivotY, upperBody);

  // Skin-coloured fists past the sleeves, so the weapon visibly sits *in* a
  // hand instead of growing out of the forearm.
  for (const arm of [leftArm, rightArm]) {
    const hand = new THREE.Mesh(
      geometry(new THREE.BoxGeometry(0.13, 0.12, 0.16)),
      material(def.skin),
    );
    hand.position.y = -(ARM_HEIGHT + 0.04);
    arm.add(hand);
  }

  // Head with two dark eyes on the forward (-z) face, so facing reads at a
  // glance even before the weapon or the nameplate does.
  upperBlock(HEAD_SIZE, HEAD_SIZE, HEAD_SIZE, def.skin, 0, HEAD_CENTER_Y, 0);
  upperBlock(0.05, 0.07, 0.02, 0x22252c, -0.08, HEAD_CENTER_Y + 0.04, -HEAD_SIZE / 2 - 0.005);
  upperBlock(0.05, 0.07, 0.02, 0x22252c, 0.08, HEAD_CENTER_Y + 0.04, -HEAD_SIZE / 2 - 0.005);

  const headTop = HEAD_CENTER_Y + HEAD_SIZE / 2;
  switch (def.accessory) {
    case 'cap':
      upperBlock(0.4, 0.08, 0.4, def.accent, 0, headTop + 0.04, 0);
      upperBlock(0.38, 0.04, 0.16, def.accent, 0, headTop, -0.26);
      break;
    case 'beanie':
      upperBlock(0.4, 0.14, 0.4, def.accent, 0, headTop + 0.05, 0);
      break;
    case 'visor': {
      const visor = new THREE.Mesh(
        geometry(new THREE.BoxGeometry(0.38, 0.12, 0.06)),
        material(def.accent, { transparent: true, opacity: 0.75 }),
      );
      visor.position.set(0, HEAD_CENTER_Y + 0.05 - LEG_HEIGHT, -HEAD_SIZE / 2 - 0.03);
      upperBody.add(visor);
      break;
    }
    case 'mohawk':
      upperBlock(0.08, 0.16, 0.34, def.accent, 0, headTop + 0.08, 0);
      break;
    case 'headphones':
      upperBlock(0.06, 0.14, 0.14, def.accent, -0.21, HEAD_CENTER_Y + 0.02, 0);
      upperBlock(0.06, 0.14, 0.14, def.accent, 0.21, HEAD_CENTER_Y + 0.02, 0);
      upperBlock(0.46, 0.04, 0.08, def.accent, 0, headTop + 0.02, 0);
      break;
    case 'hood':
      upperBlock(0.44, 0.1, 0.44, def.accent, 0, headTop + 0.05, 0);
      upperBlock(0.44, 0.36, 0.08, def.accent, 0, HEAD_CENTER_Y, HEAD_SIZE / 2 + 0.04);
      break;
  }

  // One gun per weapon slot, gripped by the fist with the barrel running down
  // the arm's local -Y — so the raised arm is automatically the aim direction,
  // and switching weapons is a visibility toggle, not a rebuild. The receiver
  // rides *above* the fist (local -z is up once the arm is raised) with a stock
  // running back over the forearm, so the hand wraps a grip instead of the
  // whole weapon being swallowed by the sleeve.
  const guns: THREE.Group[] = [];
  const muzzles: THREE.Object3D[] = [];
  const gunMetal = material(0x454d5e);
  const gunDark = material(0x2b303b);
  for (const id of WEAPON_ORDER) {
    const [width, height, length] = GUN_SIZES[id];
    const gun = new THREE.Group();
    // Origin at the centre of the fist; every part is placed around the grip.
    gun.position.set(0, -(ARM_HEIGHT + 0.04), 0);
    const lift = -(height / 2 + 0.06);

    const receiver = new THREE.Mesh(
      geometry(new THREE.BoxGeometry(width, length * 0.52, height)),
      gunMetal,
    );
    receiver.position.set(0, -(0.04 + length * 0.26), lift);
    gun.add(receiver);

    const stockLength = id === 'smg' ? 0.1 : 0.2;
    const stock = new THREE.Mesh(
      geometry(new THREE.BoxGeometry(width * 0.7, stockLength, height * 0.75)),
      gunDark,
    );
    stock.position.set(0, stockLength / 2 - 0.02, lift);
    gun.add(stock);

    // CylinderGeometry is Y-aligned already — exactly the axis the gun uses.
    const barrel = new THREE.Mesh(
      geometry(new THREE.CylinderGeometry(0.016, 0.016, length * 0.42, 8)),
      gunDark,
    );
    barrel.position.set(0, -(0.02 + length * 0.73), lift);
    gun.add(barrel);

    if (id === 'smg' || id === 'rifle') {
      const magazine = new THREE.Mesh(
        geometry(new THREE.BoxGeometry(width * 0.6, 0.1, 0.12)),
        gunDark,
      );
      magazine.position.set(0, -(0.04 + length * 0.3), lift + height / 2 + 0.05);
      gun.add(magazine);
    }

    const muzzle = new THREE.Object3D();
    muzzle.position.set(0, -(0.02 + length * 0.94), lift);
    gun.add(muzzle);

    gun.visible = false;
    rightArm.add(gun);
    guns.push(gun);
    muzzles.push(muzzle);
  }

  // Team ring at the feet — the unambiguous friend-or-foe read, matching the
  // pad circles: blue ring, red ring.
  const ring = new THREE.Mesh(
    geometry(new THREE.RingGeometry(0.32, 0.44, 28)),
    material(teamColor, { transparent: true, opacity: 0.85, side: THREE.DoubleSide }),
  );
  ring.rotation.x = -Math.PI / 2;
  ring.position.y = 0.02;
  rig.add(ring);

  return {
    group,
    rig,
    upperBody,
    leftArm,
    rightArm,
    leftLeg,
    rightLeg,
    leftKnee: left.knee,
    rightKnee: right.knee,
    guns,
    muzzles,
    resources,
  };
};

export const disposeCharacterRig = (rig: CharacterRig): void => {
  for (const resource of rig.resources) resource.dispose();
};

/**
 * Poses a rig between standing (0) and fully crouched (1) as an articulated
 * squat: thighs rotate forward at the hips, knees bend so the shins tuck back
 * under the body, and the hips sit exactly where that leg geometry puts them —
 * the feet stay planted on the ground at every blend amount. The upper body
 * leans slightly forward for balance, like a real squat. `stride` is the
 * caller's walk swing, damped while low so a crouch-walk shuffles instead of
 * marching on bent knees.
 */
/**
 * The mid-air pose, blended *over* whatever the crouch pose just set (so a
 * crouch-jump still reads): leading leg tucked high, trailing leg back — the
 * classic hurdler silhouette — with a touch of forward lean. As the arc tips
 * over (`fall` 0 rising → 1 dropping) the tuck relaxes and both legs reach
 * down for the landing.
 */
const airMix = (part: THREE.Object3D, target: number, amount: number): void => {
  part.rotation.x += (target - part.rotation.x) * amount;
};

export const applyAirPose = (rig: CharacterRig, air: number, fall: number): void => {
  if (air <= 0.001) return;
  airMix(rig.leftLeg, 1.15 - 0.7 * fall, air);
  airMix(rig.leftKnee, -1.7 + 1.0 * fall, air);
  airMix(rig.rightLeg, -0.35 + 0.2 * fall, air);
  airMix(rig.rightKnee, -0.55 + 0.3 * fall, air);
  // The arms stop pumping mid-air and hold a slight backward brace — a jump
  // with walk-swinging arms reads as running on air.
  airMix(rig.leftArm, -0.25, air);
  airMix(rig.rightArm, -0.25, air);
  rig.upperBody.rotation.x -= 0.12 * air;
};

/**
 * The slide, eased over the crouch pose: hips dropped to the floor, left leg
 * shot out straight ahead with the foot skimming the ground, right leg folded
 * under the body, torso tipped back and the left arm planted behind for
 * support. The gun arm target only shows in the lobby — in combat the aim
 * override wins, so a sliding shooter keeps tracking their target.
 */
export const applySlidePose = (rig: CharacterRig, slide: number): void => {
  if (slide <= 0.001) return;
  airMix(rig.leftLeg, 1.5, slide);
  airMix(rig.leftKnee, -0.1, slide);
  airMix(rig.rightLeg, 1.05, slide);
  airMix(rig.rightKnee, -1.55, slide);
  airMix(rig.leftArm, -0.9, slide);
  airMix(rig.rightArm, -0.35, slide);
  const hipY = rig.upperBody.position.y + (SLIDE_HIP_Y - rig.upperBody.position.y) * slide;
  rig.upperBody.position.y = hipY;
  rig.leftLeg.position.y = hipY;
  rig.rightLeg.position.y = hipY;
  rig.upperBody.rotation.x += (SLIDE_LEAN_BACK - rig.upperBody.rotation.x) * slide;
};

export const applyCrouchPose = (
  rig: CharacterRig,
  crouch: number,
  stride: number,
): void => {
  const thighAngle = CROUCH_THIGH * crouch;
  const shinAngle = CROUCH_SHIN * crouch;
  // Vertical reach of thigh + shin at these angles — feet touch the ground.
  const hipY =
    THIGH_LENGTH * Math.cos(thighAngle) + SHIN_LENGTH * Math.cos(shinAngle);
  const hipBack = CROUCH_HIP_BACK * crouch;
  rig.upperBody.position.y = hipY;
  rig.upperBody.position.z = hipBack;
  rig.upperBody.rotation.x = -CROUCH_LEAN * crouch;
  rig.leftLeg.position.y = hipY;
  rig.rightLeg.position.y = hipY;
  // The whole hip girdle — torso and leg roots — moves back together, so the
  // knees end up out front and the butt behind the heels, not a straight drop.
  rig.leftLeg.position.z = hipBack;
  rig.rightLeg.position.z = hipBack;
  rig.leftLeg.rotation.z = -CROUCH_KNEE_OUT * crouch;
  rig.rightLeg.rotation.z = CROUCH_KNEE_OUT * crouch;

  const swing = stride * (1 - 0.6 * crouch);
  rig.leftLeg.rotation.x = thighAngle + swing;
  rig.rightLeg.rotation.x = thighAngle - swing;
  // Knee bend is relative to the thigh: fold back past it until the shin ends
  // up just behind vertical in world space, heels under the hips.
  const kneeBend = -(thighAngle + shinAngle);
  rig.leftKnee.rotation.x = kneeBend;
  rig.rightKnee.rotation.x = kneeBend;

  // Arms are owned here so every pose layer composes from one base: walk swing
  // while upright, blending into a forward reach over the knees as the squat
  // deepens. The combat gun arm is overridden after the pose layers run.
  rig.leftArm.rotation.x = -stride * 0.7 * (1 - crouch) + CROUCH_ARM * crouch;
  rig.rightArm.rotation.x = stride * 0.7 * (1 - crouch) + CROUCH_ARM * crouch;
};

const makeNameSprite = (name: string, color: number): THREE.Sprite => {
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 64;
  const ctx = canvas.getContext('2d')!;
  ctx.font = 'bold 34px Inter, system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineWidth = 6;
  ctx.strokeStyle = 'rgba(0,0,0,0.85)';
  ctx.strokeText(name, 128, 32);
  ctx.fillStyle = `#${color.toString(16).padStart(6, '0')}`;
  ctx.fillText(name, 128, 32);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  const sprite = new THREE.Sprite(
    new THREE.SpriteMaterial({ map: texture, depthTest: false, transparent: true }),
  );
  sprite.scale.set(1.6, 0.4, 1);
  sprite.renderOrder = 10;
  return sprite;
};

/**
 * Renders everyone except the local player. Remote entities are drawn
 * `INTERPOLATION_DELAY_MS` in the past and interpolated between the two
 * snapshots that bracket that moment — the standard trade of a little latency
 * for perfectly smooth motion regardless of snapshot rate or jitter.
 *
 * Each player is a blocky minifig assembled from their CharacterDef — no mesh
 * assets, same philosophy as the synthesised audio. Team is shown by the name
 * label and a coloured ring at the feet, so characters are free to wear any
 * colours they like.
 */
export class PlayerRenderer {
  private readonly players = new Map<PlayerId, RemotePlayer>();
  private readonly names = new Map<PlayerId, string>();
  private readonly characters = new Map<PlayerId, number>();
  /** Mirrors `map.combat`: whether the characters here carry weapons at all. */
  private combat = false;
  /** The local player's team, for deciding whose nameplates to show. */
  private localTeam: TeamId = 0;
  /** Not rendered even while alive — the player whose eyes we are borrowing. */
  private hiddenId: PlayerId | null = null;
  /** Previous `update` timestamp, for easing pose transitions. */
  private lastMs = 0;
  /**
   * Estimated `performance.now() - serverTimeMs`, mapping the server's tick
   * clock onto the local one. Samples are stamped with *server* time — evenly
   * spaced by construction — so TCP delivering three snapshots in one burst
   * cannot collapse the interpolation timeline the way stamping them with
   * arrival time did (which was visible as remote players stuttering whenever
   * the network jittered). NaN until the first snapshot.
   */
  private clockOffsetMs = Number.NaN;

  constructor(private readonly scene: THREE.Scene) {}

  setCombat(combat: boolean): void {
    this.combat = combat;
  }

  setLocalTeam(team: TeamId): void {
    this.localTeam = team;
  }

  /** Hides one player's model (the spectated one), or none when null. */
  setHidden(id: PlayerId | null): void {
    this.hiddenId = id;
  }

  setName(id: PlayerId, name: string): void {
    this.names.set(id, name);
    const existing = this.players.get(id);
    if (existing && existing.name !== name) {
      existing.name = name;
      existing.rig.group.remove(existing.label);
      existing.label.material.map?.dispose();
      existing.label.material.dispose();
      existing.label = makeNameSprite(name, TEAM_COLORS[existing.team]);
      existing.label.position.y = PLAYER_HEIGHT + 0.35;
      existing.rig.group.add(existing.label);
    }
  }

  getName(id: PlayerId): string {
    return this.names.get(id) ?? `Player ${id}`;
  }

  getTeam(id: PlayerId): TeamId | null {
    return this.players.get(id)?.team ?? null;
  }

  setCharacter(id: PlayerId, character: number): void {
    this.characters.set(id, character);
    const existing = this.players.get(id);
    if (existing && existing.character !== character) {
      // Rebuilt lazily: the next snapshot that includes them recreates the rig.
      this.destroy(existing);
      this.players.delete(id);
    }
  }

  private create(snapshot: SnapshotPlayer): RemotePlayer {
    const teamColor = TEAM_COLORS[snapshot.team];
    const character = this.characters.get(snapshot.id) ?? 0;
    const rig = buildCharacterRig(character, teamColor);

    const name = this.getName(snapshot.id);
    const label = makeNameSprite(name, teamColor);
    label.position.y = PLAYER_HEIGHT + 0.35;
    rig.group.add(label);

    this.scene.add(rig.group);

    const player: RemotePlayer = {
      id: snapshot.id,
      team: snapshot.team,
      name,
      character,
      rig,
      label,
      samples: [],
      health: snapshot.health,
      alive: true,
      weapon: weaponIndex(snapshot.weapon),
      walkPhase: 0,
      crouchAmount: 0,
      slideAmount: 0,
      airAmount: 0,
      landDip: 0,
      prevGrounded: true,
      lastX: snapshot.position.x,
      lastZ: snapshot.position.z,
      eyeX: snapshot.position.x,
      eyeY: snapshot.position.y + PLAYER_HEIGHT - 0.15,
      eyeZ: snapshot.position.z,
      viewYaw: snapshot.yaw,
      viewPitch: snapshot.pitch,
    };
    this.players.set(snapshot.id, player);
    return player;
  }

  /**
   * Diagnostics for the F3 overlay, drained via `consumeDiag`: frames rendered
   * with no fresh sample (freeze-then-jump) and total downward clock snaps.
   */
  private diagStarvedFrames = 0;
  private diagSnapDownMs = 0;

  consumeDiag(): { starvedFrames: number; snapDownMs: number } {
    const out = {
      starvedFrames: this.diagStarvedFrames,
      snapDownMs: this.diagSnapDownMs,
    };
    this.diagStarvedFrames = 0;
    this.diagSnapDownMs = 0;
    return out;
  }

  /** Feeds one snapshot's worth of remote players into the interpolation buffers. */
  ingest(
    players: readonly SnapshotPlayer[],
    receivedAtMs: number,
    serverTick: number,
  ): void {
    const serverMs = serverTick * TICK_DT * 1000;
    const offset = receivedAtMs - serverMs;
    if (Number.isNaN(this.clockOffsetMs) || Math.abs(offset - this.clockOffsetMs) > 250) {
      // First snapshot, a match transfer (each match's tick clock starts over),
      // or waking from a background tab: nothing to smooth from, take it as is.
      this.clockOffsetMs = offset;
    } else if (offset < this.clockOffsetMs) {
      // A faster packet is a truer reading of the actual clock gap — delayed
      // ones only ever overstate it. Chase improvements immediately...
      this.diagSnapDownMs += this.clockOffsetMs - offset;
      this.clockOffsetMs = offset;
    } else {
      // ...and drift upward only slowly, so a genuinely slower path stops
      // starving the buffer without every jittered burst yanking time around.
      this.clockOffsetMs += (offset - this.clockOffsetMs) * 0.02;
    }

    const seen = new Set<PlayerId>();

    for (const snapshot of players) {
      seen.add(snapshot.id);
      const player = this.players.get(snapshot.id) ?? this.create(snapshot);
      player.health = snapshot.health;
      player.alive = (snapshot.flags & SnapshotFlag.Alive) !== 0;
      player.weapon = weaponIndex(snapshot.weapon);
      player.samples.push({
        timeMs: serverMs,
        x: snapshot.position.x,
        y: snapshot.position.y,
        z: snapshot.position.z,
        yaw: snapshot.yaw,
        pitch: snapshot.pitch,
        crouched:
          (snapshot.flags & (SnapshotFlag.Crouching | SnapshotFlag.Sliding)) !== 0,
        grounded: (snapshot.flags & SnapshotFlag.OnGround) !== 0,
        sliding: (snapshot.flags & SnapshotFlag.Sliding) !== 0,
      });
      // A second of history is far more than the interpolation delay needs.
      if (player.samples.length > 64) player.samples.shift();
    }

    for (const [id, player] of this.players) {
      if (seen.has(id)) continue;
      this.destroy(player);
      this.players.delete(id);
    }
  }

  remove(id: PlayerId): void {
    const player = this.players.get(id);
    if (!player) return;
    this.destroy(player);
    this.players.delete(id);
    this.names.delete(id);
    this.characters.delete(id);
  }

  /** Interpolated world position of a remote player, for effects like tracers. */
  positionOf(id: PlayerId, out: THREE.Vector3): boolean {
    const player = this.players.get(id);
    if (!player) return false;
    out.copy(player.rig.group.position);
    return true;
  }

  /** Living players on `team`, in stable id order — the spectate rotation. */
  spectateCandidates(team: TeamId): PlayerId[] {
    const out: PlayerId[] = [];
    for (const player of this.players.values()) {
      if (player.alive && player.team === team) out.push(player.id);
    }
    return out.toSorted((a, b) => a - b);
  }

  /** The interpolated first-person eye pose of a living player, for spectating. */
  spectatePose(id: PlayerId, out: SpectatePose): boolean {
    const player = this.players.get(id);
    if (!player || !player.alive || player.samples.length === 0) return false;
    out.x = player.eyeX;
    out.y = player.eyeY;
    out.z = player.eyeZ;
    out.yaw = player.viewYaw;
    out.pitch = player.viewPitch;
    return true;
  }

  /** Weapon index a player is holding, for the spectator's view model. */
  weaponOf(id: PlayerId): number | null {
    return this.players.get(id)?.weapon ?? null;
  }

  /**
   * Distance along the ray to the nearest living enemy it crosses, or null.
   * Drives touch auto-fire, so it deliberately tests the *rendered* pose — the
   * one the player is actually looking at — with a slightly padded box; the
   * server still decides whether the resulting shot lands. The caller checks
   * walls; this only answers "is an enemy under the crosshair".
   */
  enemyUnderRay(
    team: TeamId,
    ox: number,
    oy: number,
    oz: number,
    dx: number,
    dy: number,
    dz: number,
    maxRange: number,
    /** Extra hitbox padding: small for the trigger, generous for aim assist. */
    pad = 0.15,
  ): number | null {
    const half = PLAYER_RADIUS + pad;
    let best: number | null = null;

    for (const player of this.players.values()) {
      if (!player.alive || player.team === team) continue;
      const at = player.rig.group.position;
      const height =
        lerp(PLAYER_HEIGHT, PLAYER_CROUCH_HEIGHT, player.crouchAmount) + pad;

      // Slab test; ±Infinity from a zero component orders correctly in JS.
      let tMin = -Infinity;
      let tMax = Infinity;
      const axes: [number, number, number, number][] = [
        [ox, dx, at.x - half, at.x + half],
        [oy, dy, at.y, at.y + height],
        [oz, dz, at.z - half, at.z + half],
      ];
      for (const [origin, dir, min, max] of axes) {
        const inv = 1 / dir;
        const t1 = (min - origin) * inv;
        const t2 = (max - origin) * inv;
        tMin = Math.max(tMin, Math.min(t1, t2));
        tMax = Math.min(tMax, Math.max(t1, t2));
      }
      if (tMax < Math.max(tMin, 0) || tMin > maxRange) continue;
      const distance = Math.max(tMin, 0);
      if (best === null || distance < best) best = distance;
    }
    return best;
  }

  /**
   * World position of the visible gun's barrel tip, so remote tracers leave the
   * weapon the player sees, not the shooter's eyes. False when the shooter has
   * no rendered, armed model to anchor to (caller falls back to the wire origin).
   */
  muzzleWorldPosition(id: PlayerId, out: THREE.Vector3): boolean {
    const player = this.players.get(id);
    if (!player || !player.alive || !this.combat || player.samples.length === 0) {
      return false;
    }
    const muzzle = player.rig.muzzles[player.weapon];
    if (!muzzle) return false;
    muzzle.getWorldPosition(out);
    return true;
  }

  update(nowMs: number): void {
    // Render on the server's clock, a fixed delay in its past — arrival jitter
    // moves the offset estimate a little, never the spacing of the samples.
    const renderTime = nowMs - this.clockOffsetMs - INTERPOLATION_DELAY_MS;
    const dt = this.lastMs === 0 ? 0 : Math.min(0.1, (nowMs - this.lastMs) / 1000);
    this.lastMs = nowMs;

    for (const player of this.players.values()) {
      player.rig.group.visible = player.alive && player.id !== this.hiddenId;
      // Nameplates float over everyone in the lobby, but in a match reading an
      // enemy's name through a fight is a free wallhack-adjacent tell — only
      // teammates keep theirs.
      player.label.visible = !this.combat || player.team === this.localTeam;
      if (!player.alive || player.samples.length === 0) continue;

      const samples = player.samples;
      let older = samples[0]!;
      let newer = samples[samples.length - 1]!;
      for (let i = samples.length - 1; i >= 0; i--) {
        if (samples[i]!.timeMs <= renderTime) {
          older = samples[i]!;
          newer = samples[i + 1] ?? samples[i]!;
          break;
        }
      }

      const span = newer.timeMs - older.timeMs;
      // Clamped so a stalled stream extrapolates by at most one snapshot.
      const t = span > 0 ? Math.min(1, (renderTime - older.timeMs) / span) : 1;
      // The stream fell behind the render clock: this frame shows a frozen
      // pose and the catch-up will read as a jump. Counted for the F3 overlay.
      if (newer.timeMs < renderTime) this.diagStarvedFrames++;

      player.rig.group.position.set(
        lerp(older.x, newer.x, t),
        lerp(older.y, newer.y, t),
        lerp(older.z, newer.z, t),
      );
      player.rig.group.rotation.y = lerpAngle(older.yaw, newer.yaw, t);

      // Gait driven by distance covered, so limbs swing exactly as fast as the
      // player moves — sprinting pumps, creeping barely sways, idling settles.
      const moved = Math.hypot(
        player.rig.group.position.x - player.lastX,
        player.rig.group.position.z - player.lastZ,
      );
      player.lastX = player.rig.group.position.x;
      player.lastZ = player.rig.group.position.z;
      player.walkPhase += moved * 5.5;
      const swing = Math.min(0.9, moved * 60) * Math.sin(player.walkPhase);

      // Ease into the kneel rather than snapping on the first crouched
      // snapshot — the pose is client-side dressing over a binary wire flag.
      player.crouchAmount = clamp(
        player.crouchAmount + (newer.crouched ? dt : -dt) * 8,
        0,
        1,
      );
      // The slide snaps in faster than the crouch — it is an impulse move, and
      // easing it slowly would show a beat of crouch-walk before the lean-back.
      player.slideAmount = clamp(
        player.slideAmount + (newer.sliding ? dt : -dt) * 12,
        0,
        1,
      );

      // Jump animation: tuck in the air, squash briefly on touchdown. All
      // derived from the wire's OnGround flag and the interpolated arc — the
      // sim knows nothing about any of it.
      const grounded = newer.grounded;
      if (grounded && !player.prevGrounded && player.airAmount > 0.25) {
        player.landDip = 0.45;
      }
      player.prevGrounded = grounded;
      player.airAmount = clamp(player.airAmount + (grounded ? -dt : dt) * 11, 0, 1);
      player.landDip *= Math.exp(-9 * dt);
      const vy = span > 0 ? ((newer.y - older.y) / span) * 1000 : 0;

      applyCrouchPose(
        player.rig,
        Math.min(1, player.crouchAmount + player.landDip),
        swing,
      );
      applyAirPose(player.rig, player.airAmount, clamp(0.5 - vy / 16, 0, 1));
      applySlidePose(player.rig, player.slideAmount);

      // In combat the right arm is the gun arm: raised level and tilted with
      // the interpolated view pitch, so everyone can read where a player is
      // pointing. The upper body's crouch lean is subtracted back out so the
      // weapon keeps tracking the view. In the lobby there is nothing to hold
      // and both arms swing.
      const armed = this.combat;
      const pitch = lerp(older.pitch, newer.pitch, t);
      for (let i = 0; i < player.rig.guns.length; i++) {
        player.rig.guns[i]!.visible = armed && i === player.weapon;
      }
      // Unarmed, the pose layers own both arms; armed, the gun arm overrides.
      if (armed) {
        player.rig.rightArm.rotation.x =
          Math.PI / 2 + pitch - player.rig.upperBody.rotation.x;
      }

      const height = newer.crouched ? PLAYER_CROUCH_HEIGHT : PLAYER_HEIGHT;
      player.label.position.y =
        lerp(PLAYER_HEIGHT, PLAYER_CROUCH_HEIGHT, player.crouchAmount) + 0.35;

      // Stash the pose the spectator camera borrows — same eye offset the sim
      // uses (`eyePosition`), so a spectator sees what the player sees.
      player.eyeX = player.rig.group.position.x;
      player.eyeY = player.rig.group.position.y + height - 0.15;
      player.eyeZ = player.rig.group.position.z;
      player.viewYaw = player.rig.group.rotation.y;
      player.viewPitch = pitch;
    }
  }

  private destroy(player: RemotePlayer): void {
    this.scene.remove(player.rig.group);
    disposeCharacterRig(player.rig);
    player.label.material.map?.dispose();
    player.label.material.dispose();
  }

  clear(): void {
    for (const player of this.players.values()) this.destroy(player);
    this.players.clear();
    this.names.clear();
    this.characters.clear();
    // The next match's tick clock starts over; recalibrate from its first snapshot.
    this.clockOffsetMs = Number.NaN;
  }
}
